/**
 * DistrictStats: the district statistics panel for the active plan.
 *
 * One row per district — color chip, label, population, deviation from
 * ideal (people and %), and a visual deviation bar — plus a plain-language
 * summary. Rows are clickable: selecting a row makes that district the
 * active paint target (this panel replaces the old DistrictPalette).
 *
 * Honesty about provenance:
 *  - Population totals and deviations are LIVE ESTIMATES computed in the
 *    browser from census block data as you draw.
 *  - Contiguity and compactness (Polsby-Popper) appear only after a server
 *    validation run, badged "Validated". Painting after validating marks
 *    the report stale until you re-validate.
 *
 * Deviation bands use the tenant's configured `max_population_deviation_pct`
 * (backend default 10 when absent): green within half the threshold, amber
 * within the threshold, red beyond it.
 */
import { useMemo, useState } from 'react';
import { DemographicsStats } from './DemographicsStats';
import { usePaintingStore, selectIdealPopulation } from '../../stores/painting';
import { useTenantStore } from '../../stores/tenant';
import {
  computeDistrictStats,
  formatDeviation,
  formatPopulation,
  type DistrictStat,
} from '../../lib/districtStats';
import type { DistrictValidationResult } from '../../lib/plans';
import { readableTextOn } from '../../lib/districtColors';
import { DISTRICT_LABEL_MAX } from '../../lib/plans';
import { PencilIcon } from '../icons';

/** Fallback when the tenant config omits the deviation threshold (matches backend). */
const DEFAULT_MAX_DEVIATION_PCT = 10;

function formatSignedCount(value: number): string {
  const rounded = Math.round(value);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toLocaleString('en-US')}`;
}

function bandColor(absPct: number, warnAt: number, threshold: number): string {
  if (absPct <= warnAt) return 'bg-emerald-500';
  if (absPct <= threshold) return 'bg-amber-500';
  return 'bg-red-500';
}

function bandText(absPct: number, warnAt: number, threshold: number): string {
  if (absPct <= warnAt) return 'text-emerald-700';
  if (absPct <= threshold) return 'text-amber-700';
  return 'font-medium text-red-600';
}

function DeviationBar(props: {
  pct: number;
  maxAbs: number;
  warnAt: number;
  threshold: number;
}): JSX.Element {
  const { pct, maxAbs, warnAt, threshold } = props;
  const clamped = Math.max(-maxAbs, Math.min(maxAbs, pct));
  const halfWidth = maxAbs > 0 ? (Math.abs(clamped) / maxAbs) * 50 : 0;
  const style =
    clamped >= 0
      ? { left: '50%', width: `${halfWidth}%` }
      : { right: '50%', width: `${halfWidth}%` };
  // Threshold tick positions (percent of full width).
  const tickAt = (t: number): number => 50 + (Math.min(t, maxAbs) / maxAbs) * 50;
  const tickStyle = (t: number): React.CSSProperties =>
    t >= 0
      ? { left: `${tickAt(t)}%` }
      : { right: `${tickAt(-t)}%` };
  return (
    <div
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-stone-200/70"
      role="img"
      aria-label={`Deviation ${formatDeviation(pct)} from the ideal district population`}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-1/2 w-px bg-stone-400" />
      <span aria-hidden="true" className="absolute inset-y-0 w-px bg-stone-400/60" style={tickStyle(threshold)} />
      <span aria-hidden="true" className="absolute inset-y-0 w-px bg-stone-400/60" style={tickStyle(-threshold)} />
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 rounded-full ${bandColor(Math.abs(pct), warnAt, threshold)}`}
        style={style}
      />
    </div>
  );
}

function SourceBadge(props: { validated: boolean; stale: boolean }): JSX.Element {
  if (props.validated && !props.stale) {
    return (
      <span
        className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800"
        title="Population, contiguity, and compactness were checked by the server for the current drawing."
      >
        ✓ Validated
      </span>
    );
  }
  if (props.validated && props.stale) {
    return (
      <span
        className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
        title="You changed the drawing after the last validation — re-validate to refresh these figures."
      >
        Validated · outdated
      </span>
    );
  }
  return (
    <span
      className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-600"
      title="Computed live in your browser from census block data as you draw. Run Validate for the server's official check."
    >
      Live estimate
    </span>
  );
}

function PopulationStats(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const planId = usePaintingStore((s) => s.planId);
  const districtCount = usePaintingStore((s) => s.districtCount);
  const colors = usePaintingStore((s) => s.colors);
  const labels = usePaintingStore((s) => s.labels);
  const activeDistrict = usePaintingStore((s) => s.activeDistrict);
  const assignments = usePaintingStore((s) => s.assignments);
  const populations = usePaintingStore((s) => s.populations);
  const populationsState = usePaintingStore((s) => s.populationsState);
  const totalPopulation = usePaintingStore((s) => s.totalPopulation);
  const ideal = usePaintingStore(selectIdealPopulation);
  const setActiveDistrict = usePaintingStore((s) => s.setActiveDistrict);
  const defaultLabels = usePaintingStore((s) => s.defaultLabels);
  const customLabels = usePaintingStore((s) => s.customLabels);
  const setDistrictLabel = usePaintingStore((s) => s.setDistrictLabel);
  const canRename = usePaintingStore((s) => s.planStatus !== 'submitted');
  const [editing, setEditing] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const validationReport = usePaintingStore((s) => s.validationReport);
  const validationStale = usePaintingStore((s) => s.validationStale);

  const threshold = tenant?.districts.max_population_deviation_pct ?? DEFAULT_MAX_DEVIATION_PCT;
  const warnAt = threshold / 2;

  const stats: DistrictStat[] = useMemo(
    () => computeDistrictStats(assignments, populations, districtCount),
    [assignments, populations, districtCount],
  );

  const validatedByDistrict = useMemo(() => {
    const map = new Map<number, DistrictValidationResult>();
    for (const d of validationReport?.districts ?? []) {
      map.set(d.district, d);
    }
    return map;
  }, [validationReport]);

  const maxAbsDev = useMemo(
    () => Math.max(threshold * 1.5, ...stats.map((s) => Math.abs(s.deviationPct)), 0.5),
    [stats, threshold],
  );

  const largestDev = useMemo(() => {
    let best: DistrictStat | null = null;
    for (const s of stats) {
      if (!best || Math.abs(s.deviationPct) > Math.abs(best.deviationPct)) best = s;
    }
    return best;
  }, [stats]);

  if (!planId || districtCount === 0) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50/60 p-4 text-center">
        <p className="text-sm font-medium text-stone-700">No district statistics yet</p>
        <p className="mt-1 text-xs text-stone-500">
          Create or open a plan, then paint blocks on the map — the numbers update live as you
          draw.
        </p>
      </div>
    );
  }

  const validated = validationReport !== null;
  const assignedBlocks = Object.keys(assignments).length;

  return (
    <section aria-label="District statistics">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-ink">Districts</h2>
        <SourceBadge validated={validated} stale={validationStale} />
      </div>

      {populationsState === 'loading' && (
        <p className="mb-2 text-xs text-stone-500">Loading block populations…</p>
      )}
      {populationsState === 'error' && (
        <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Population data unavailable — statistics are hidden until it loads.
        </p>
      )}

      {populationsState === 'ready' && (
        <p className="mb-3 text-xs leading-relaxed text-stone-500">
          Each district should hold about{' '}
          <span className="font-medium text-stone-700">{formatPopulation(ideal)} people</span>.
          Bars show how far each district is from that ideal — your jurisdiction requires every
          district within <span className="font-medium text-stone-700">±{threshold}%</span>.
        </p>
      )}

      <ul className="space-y-2">
        {stats.map((stat) => {
          const color = colors[stat.district] ?? '#64748b';
          const label = labels[stat.district] ?? `District ${stat.district + 1}`;
          const isActive = stat.district === activeDistrict;
          const absDev = Math.abs(stat.deviationPct);
          const validatedRow = validatedByDistrict.get(stat.district);
          const devCount = populationsState === 'ready' ? stat.population - ideal : 0;
          return (
            <li
              key={stat.district}
              style={isActive ? ({ '--tw-ring-color': color } as React.CSSProperties) : undefined}
              className={`relative rounded-xl border px-3 py-2.5 transition ${
                isActive
                  ? 'border-transparent bg-white shadow-sm ring-2'
                  : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/70'
              }`}
            >
              {/* The whole card selects the district; the rename control sits above it. */}
              <button
                type="button"
                onClick={() => setActiveDistrict(stat.district)}
                aria-pressed={isActive}
                aria-label={`${label}: ${formatPopulation(stat.population)} people, deviation ${formatDeviation(stat.deviationPct)}. Select as paint target.`}
                title="Click to paint with this district"
                className="absolute inset-0 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tenant-primary"
              />
              <span className="pointer-events-none relative block">
                <span className="flex items-center gap-2.5">
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold shadow-sm"
                    style={{ backgroundColor: color, color: readableTextOn(color) }}
                  >
                    {stat.district + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      {editing === stat.district ? (
                        <input
                          autoFocus
                          value={draftName}
                          maxLength={DISTRICT_LABEL_MAX}
                          placeholder={defaultLabels[stat.district] ?? `District ${stat.district + 1}`}
                          aria-label={`Name for district ${stat.district + 1}`}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => {
                            setDistrictLabel(stat.district, draftName.trim());
                            setEditing(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') {
                              e.stopPropagation();
                              setEditing(null);
                            }
                          }}
                          className="pointer-events-auto -my-1 h-7 min-w-0 flex-1 rounded-md border border-tenant-primary bg-white px-1.5 text-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-tenant-tint"
                        />
                      ) : (
                        <span className="flex min-w-0 items-center gap-1">
                          <span className="truncate text-sm font-medium text-ink">{label}</span>
                          {canRename && (
                            <button
                              type="button"
                              onClick={() => {
                                setDraftName(customLabels[stat.district] ?? '');
                                setEditing(stat.district);
                              }}
                              aria-label={`Rename ${label}`}
                              title="Rename this district"
                              className="pointer-events-auto -m-1 shrink-0 rounded p-1 text-stone-300 hover:bg-stone-100 hover:text-stone-600 focus-visible:text-stone-600"
                            >
                              <PencilIcon size={13} />
                            </button>
                          )}
                        </span>
                      )}
                      {isActive && editing !== stat.district && (
                        <span className="shrink-0 rounded-full bg-ink px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          brush
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 flex items-baseline justify-between gap-2 text-xs">
                      <span className="tabular-nums text-stone-600">
                        {populationsState === 'ready' ? (
                          <>
                            {formatPopulation(stat.population)}{' '}
                            <span className="text-stone-400">({formatSignedCount(devCount)})</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </span>
                      {populationsState === 'ready' && (
                        <span className={`tabular-nums ${bandText(absDev, warnAt, threshold)}`}>
                          {formatDeviation(stat.deviationPct)}
                        </span>
                      )}
                    </span>
                  </span>
                </span>
                {populationsState === 'ready' && (
                  <span className="mt-2 block">
                    <DeviationBar pct={stat.deviationPct} maxAbs={maxAbsDev} warnAt={warnAt} threshold={threshold} />
                  </span>
                )}
                {validatedRow && (
                  <span
                    className={`mt-1.5 flex items-center gap-2 text-[11px] ${
                      validationStale ? 'text-stone-400' : 'text-stone-500'
                    }`}
                  >
                    {validatedRow.contiguous ? (
                      <span className={validationStale ? '' : 'text-emerald-700'}>✓ Contiguous</span>
                    ) : (
                      <span className="font-medium text-red-600">
                        {validatedRow.components > 1
                          ? `⚠ Split into ${validatedRow.components} pieces`
                          : '⚠ Not contiguous'}
                      </span>
                    )}
                    <span aria-hidden="true">·</span>
                    <span>
                      Compactness{' '}
                      {validatedRow.compactness_polsby_popper !== null
                        ? validatedRow.compactness_polsby_popper.toFixed(2)
                        : '—'}
                    </span>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {populationsState === 'ready' && (
        <div className="mt-3 rounded-xl bg-stone-100/80 px-3 py-2.5 text-xs text-stone-600">
          <dl className="space-y-1">
            <div className="flex justify-between gap-2">
              <dt>Jurisdiction total</dt>
              <dd className="font-medium tabular-nums text-ink">
                {formatPopulation(totalPopulation)}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Ideal per district</dt>
              <dd className="font-medium tabular-nums text-ink">{formatPopulation(ideal)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Largest deviation</dt>
              <dd
                className={`font-medium tabular-nums ${
                  largestDev
                    ? bandText(Math.abs(largestDev.deviationPct), warnAt, threshold)
                    : 'text-ink'
                }`}
              >
                {largestDev
                  ? `${labels[largestDev.district] ?? `District ${largestDev.district + 1}`}: ${formatDeviation(largestDev.deviationPct)}`
                  : '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Blocks assigned</dt>
              <dd className="tabular-nums text-ink">
                {assignedBlocks.toLocaleString('en-US')}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  );
}

type StatsTab = 'population' | 'demographics';
const TAB_KEY = 'stats:tab';

/**
 * DistrictStats: the stats panel. "Balance" shows population vs. the ideal
 * (and validation results); "Demographics" shows VAP, CVAP and race.
 */
export function DistrictStats(): JSX.Element {
  const planId = usePaintingStore((s) => s.planId);
  const [tab, setTab] = useState<StatsTab>(() => {
    try {
      return window.sessionStorage.getItem(TAB_KEY) === 'demographics' ? 'demographics' : 'population';
    } catch {
      return 'population';
    }
  });
  if (!planId) return <PopulationStats />;

  function choose(next: StatsTab): void {
    setTab(next);
    try {
      window.sessionStorage.setItem(TAB_KEY, next);
    } catch {
      // ignore
    }
  }

  return (
    <div>
      <div className="mb-3 flex gap-4 border-b border-stone-200" role="tablist" aria-label="District statistics">
        {(
          [
            ['population', 'Balance'],
            ['demographics', 'Demographics'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => choose(key)}
            className={`-mb-px border-b-2 pb-2 text-sm font-semibold transition-colors ${
              tab === key ? 'border-tenant-primary text-ink' : 'border-transparent text-stone-500 hover:text-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel">{tab === 'population' ? <PopulationStats /> : <DemographicsStats />}</div>
    </div>
  );
}
